import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import MentorshipLinkManager from './MentorshipLinkManager';

import { MessageSquare, Upload, UserCheck, Plus, ShieldAlert, AlertCircle, UserX, Activity, ExternalLink } from 'lucide-react';

const SettingsTab = ({
  telegramLinks,
  newTelegramLink,
  setNewTelegramLink,
  handleUpdateTelegramLink,
  adminUsers,
  setAdminUsers,
  newAdminUser,
  setNewAdminUser,
  showCreateUserForm,
  setShowCreateUserForm,
  hasPermission,
  currentAdmin,
  adminExists,
  expiredUsers,
  handleRemoveExpiredUsers,
  API_URL
}) => {
  return (
          <div className="space-y-6">
            
            {/* Super Admin Setup - ONE TIME ONLY */}
            <Card className={adminExists ? "bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 shadow-lg" : "bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-500 shadow-lg"}>
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center text-xl">
                  <ShieldAlert className="w-6 h-6 mr-2 text-red-600" />
                  {adminExists ? "✅ Super Admin Already Exists" : "🔐 First Time Setup: Create Super Admin"}
                </CardTitle>
                <CardDescription className="text-gray-700 font-medium">
                  {adminExists ? "Your super admin account is ready!" : "Create YOUR super admin account (one-time only)"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {adminExists ? (
                  <div className="p-4 bg-green-100 border border-green-300 rounded-lg">
                    <p className="text-green-900 font-semibold mb-3">✅ Super Admin Setup Complete!</p>
                    <p className="text-green-800 text-sm mb-3">
                      Your super admin account has been created successfully. You can now:
                    </p>
                    <ul className="list-disc list-inside text-green-800 text-sm space-y-1 mb-3">
                      <li>Login using your email and password via the API</li>
                      <li>Create managers and sub-users in the section below</li>
                      <li>Control permissions for each user (hide revenue, limit access, etc.)</li>
                    </ul>
                    <p className="text-green-700 text-xs italic">
                      📧 If you've lost your credentials, check with the administrator or reset via database.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="p-4 bg-red-100 border border-red-300 rounded-lg mb-4">
                      <p className="text-red-900 font-semibold mb-2">⚠️ Important:</p>
                      <p className="text-red-800 text-sm">This is a ONE-TIME setup. Once you create your super admin account, this form will no longer work. You'll use your super admin account to create other managers.</p>
                    </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-800 font-semibold">Your Email *</Label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      id="super-admin-email"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-800 font-semibold">Your Full Name *</Label>
                    <Input
                      placeholder="Your Name"
                      id="super-admin-name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-800 font-semibold">Password *</Label>
                    <Input
                      type="password"
                      placeholder="Secure password"
                      id="super-admin-password"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-800 font-semibold">Setup Key *</Label>
                    <Input
                      type="password"
                      placeholder="uppercircuit-admin-2024"
                      id="super-admin-key"
                      defaultValue="uppercircuit-admin-2024"
                      className="mt-1"
                    />
                  </div>
                </div>

                <Button onClick={async () => {
                  const email = document.getElementById('super-admin-email').value;
                  const name = document.getElementById('super-admin-name').value;
                  const password = document.getElementById('super-admin-password').value;
                  const setupKey = document.getElementById('super-admin-key').value;

                  if (!email || !name || !password || !setupKey) {
                    alert('❌ Please fill all fields');
                    return;
                  }

                  try {
                    const response = await fetch(`${API_URL}/api/admin/setup/initial`, {
                      method: 'POST',
                      headers: {'Content-Type': 'application/json'},
                      body: JSON.stringify({
                        setup_key: setupKey,
                        email: email,
                        password: password,
                        full_name: name
                      })
                    });

                    const data = await response.json();

                    if (response.ok) {
                      alert(`✅ Super Admin Created!\n\nEmail: ${email}\nPassword: ${password}\n\nSave these credentials! You'll use them to login and create managers.`);
                      // Clear form
                      document.getElementById('super-admin-email').value = '';
                      document.getElementById('super-admin-name').value = '';
                      document.getElementById('super-admin-password').value = '';
                      window.location.reload();
                    } else {
                      // Handle error message properly
                      let errorMsg = 'Setup failed. Admin user might already exist.';
                      if (data.detail) {
                        if (typeof data.detail === 'string') {
                          errorMsg = data.detail;
                        } else if (Array.isArray(data.detail)) {
                          errorMsg = data.detail.map(err => err.msg || JSON.stringify(err)).join('\n');
                        } else {
                          errorMsg = JSON.stringify(data.detail);
                        }
                      }
                      alert(`❌ ${errorMsg}`);
                    }
                  } catch (error) {
                    console.error('Error:', error);
                    alert('❌ Error creating super admin. Please check console for details.');
                  }
                }} className="bg-red-600 hover:bg-red-700 text-lg">
                  🔐 Create My Super Admin Account
                </Button>

                    <p className="text-xs text-red-700 mt-3">
                      💡 After creating your super admin account, login and use the section below to add managers with custom permissions.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
            
            {/* Admin User Management - PRIORITY FIRST */}
            {hasPermission('manage_admin_users') && (
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-500 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center text-xl">
                    <UserCheck className="w-6 h-6 mr-2 text-blue-600" />
                    👥 Manage Admin Users
                  </CardTitle>
                  <CardDescription className="text-gray-700 font-medium">
                    Add managers, traders, or viewers to help run the platform
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Button onClick={() => setShowCreateUserForm(!showCreateUserForm)} size="lg" className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {showCreateUserForm ? 'Hide Form' : 'Add New Admin User'}
                    </Button>
                </div>

                {/* Create User Form */}
                {showCreateUserForm && (
                  <div className="border-2 border-blue-200 rounded-lg p-6 mb-4 bg-white shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-4 text-lg">Create New Admin User</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-800 font-semibold">Email *</Label>
                        <Input
                          type="email"
                          placeholder="manager@example.com"
                          value={newAdminUser.email}
                          onChange={(e) => setNewAdminUser({...newAdminUser, email: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-800 font-semibold">Full Name *</Label>
                        <Input
                          placeholder="John Doe"
                          value={newAdminUser.full_name}
                          onChange={(e) => setNewAdminUser({...newAdminUser, full_name: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-800 font-semibold">Password *</Label>
                        <Input
                          type="password"
                          placeholder="Secure password"
                          value={newAdminUser.password}
                          onChange={(e) => setNewAdminUser({...newAdminUser, password: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-800 font-semibold">Role *</Label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md mt-1 font-medium"
                          value={newAdminUser.role}
                          onChange={(e) => setNewAdminUser({...newAdminUser, role: e.target.value})}
                        >
                          <option value="manager">👔 Manager (Full Access - No Delete)</option>
                          <option value="trader">📈 Trader (Trades Only)</option>
                          <option value="viewer">👁️ Viewer (Read Only)</option>
                        </select>
                      </div>
                    </div>

                    {/* Custom Permissions - Detailed Control */}
                    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg">
                      <p className="text-sm text-blue-900 font-bold mb-3">📋 Customize Permissions (Check/Uncheck as needed):</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Analytics & Revenue */}
                        <div className="bg-white p-3 rounded border border-blue-200">
                          <p className="font-semibold text-gray-800 mb-2">📊 Analytics & Revenue</p>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.view_analytics}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, view_analytics: e.target.checked}})}
                            />
                            View Analytics
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.view_revenue}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, view_revenue: e.target.checked}})}
                            />
                            <span className="font-semibold text-red-600">View Revenue 💰 (Important!)</span>
                          </label>
                        </div>

                        {/* Trades */}
                        <div className="bg-white p-3 rounded border border-blue-200">
                          <p className="font-semibold text-gray-800 mb-2">📈 Trade Signals</p>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.view_trades}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, view_trades: e.target.checked}})}
                            />
                            View Trades
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.create_trades}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, create_trades: e.target.checked}})}
                            />
                            Create Trades
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.update_trades}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, update_trades: e.target.checked}})}
                            />
                            Update Trades
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.delete_trades}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, delete_trades: e.target.checked}})}
                            />
                            Delete Trades
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.send_performance}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, send_performance: e.target.checked}})}
                            />
                            Send Performance
                          </label>
                        </div>

                        {/* Payments & Subscribers */}
                        <div className="bg-white p-3 rounded border border-blue-200">
                          <p className="font-semibold text-gray-800 mb-2">💳 Payments & Subscribers</p>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.view_payments}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, view_payments: e.target.checked}})}
                            />
                            View Payments
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.view_subscribers}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, view_subscribers: e.target.checked}})}
                            />
                            View Subscribers
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.add_subscribers}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, add_subscribers: e.target.checked}})}
                            />
                            Add Subscribers
                          </label>
                          <label className="flex items-center gap-2 text-sm py-1">
                            <input 
                              type="checkbox" 
                              checked={newAdminUser.custom_permissions.edit_subscribers}
                              onChange={(e) => setNewAdminUser({...newAdminUser, custom_permissions: {...newAdminUser.custom_permissions, edit_subscribers: e.target.checked}})}
                            />
                            Edit Subscribers
                          </label>
                        </div>
                      </div>

                      <div className="mt-3 p-2 bg-yellow-100 rounded text-xs text-yellow-900">
                        💡 <strong>Tip:</strong> Uncheck "View Revenue" to hide revenue data from this manager. They'll still see analytics but not financial amounts.
                      </div>
                    </div>

                    <div className="mt-6 flex gap-3">
                      <Button onClick={async () => {
                        if (!newAdminUser.email || !newAdminUser.password || !newAdminUser.full_name) {
                          alert('❌ Please fill all required fields');
                          return;
                        }

                        try {
                          const sessionToken = prompt('🔐 Enter your super admin session token:');
                          if (!sessionToken) return;

                          const response = await fetch(`${API_URL}/api/admin/users/create?creator_session=${sessionToken}`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify(newAdminUser)
                          });

                          if (response.ok) {
                            alert('✅ Admin user created successfully!');
                            setShowCreateUserForm(false);
                            setNewAdminUser({
                              email: '',
                              password: '',
                              full_name: '',
                              role: 'manager'
                            });
                          } else {
                            const error = await response.json();
                            alert(`❌ Failed: ${error.detail}`);
                          }
                        } catch (error) {
                          console.error('Error creating user:', error);
                          alert('❌ Error creating admin user');
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700"
                      >
                        ✅ Create Admin User
                      </Button>
                      <Button variant="outline" onClick={() => setShowCreateUserForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Quick Setup Guide */}
                <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                  <h4 className="font-bold text-yellow-900 mb-2 flex items-center">
                    📖 Quick Setup Guide
                  </h4>
                  <ol className="text-sm text-yellow-800 space-y-2 ml-4 list-decimal">
                    <li><strong>Create YOUR super admin account</strong> (one-time, see docs)</li>
                    <li><strong>Login and get session token</strong></li>
                    <li><strong>Click "Add New Admin User"</strong> and fill the form above</li>
                    <li><strong>Provide session token</strong> when prompted</li>
                    <li>✅ <strong>Done!</strong> New manager can now login</li>
                  </ol>
                  <p className="text-xs text-yellow-700 mt-3 font-semibold">
                    📄 Full documentation: /app/ADMIN_USER_MANAGEMENT.md
                  </p>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Mentorship Program Link */}
            <MentorshipLinkManager API_URL={API_URL} />

            {/* Update Telegram Links */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Manage Telegram Links
                </CardTitle>
                <CardDescription className="text-gray-600">Update Telegram group links for different services</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateTelegramLink} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="subscription_type" className="text-gray-700">Service Type</Label>
                    <select
                      id="subscription_type"
                      value={newTelegramLink.subscription_type}
                      onChange={(e) => setNewTelegramLink({...newTelegramLink, subscription_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="options_only">Options Only (Monthly)</option>
                      <option value="options_only_3month">Options Only (Quarterly)</option>
                      <option value="complete_package">Complete Package (Monthly)</option>
                      <option value="complete_package_3month">Complete Package (Quarterly)</option>
                      <option value="equity_only">Equity Only (Monthly)</option>
                      <option value="equity_only_3month">Equity Only (Quarterly)</option>
                      <option value="workshop">Workshop</option>
                      <option value="index_options_educational">Index Options (Educational)</option>
                      <option value="automated_alerts_1day">Automated Alerts (1 Day)</option>
                      <option value="automated_alerts_3day">Automated Alerts (3 Day)</option>
                      <option value="automated_alerts_5day">Automated Alerts (5 Day)</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="link" className="text-gray-700">Telegram Link</Label>
                    <Input
                      id="link"
                      value={newTelegramLink.link}
                      onChange={(e) => setNewTelegramLink({...newTelegramLink, link: e.target.value})}
                      placeholder="https://t.me/+xxxxxxxxxxxxxx"
                      className="border-gray-300"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="description" className="text-gray-700">Description (Optional)</Label>
                    <Input
                      id="description"
                      value={newTelegramLink.description}
                      onChange={(e) => setNewTelegramLink({...newTelegramLink, description: e.target.value})}
                      placeholder="Brief description of this group"
                      className="border-gray-300"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      Update Telegram Link
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Current Telegram Links */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900">Current Telegram Links</CardTitle>
                <CardDescription className="text-gray-600">All configured Telegram group links</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {telegramLinks.map((link, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge variant="outline" className="border-gray-300 text-gray-700 mb-2">
                            {link.subscription_type}
                          </Badge>
                          <p className="text-sm text-gray-900 font-medium break-all">{link.link}</p>
                          {link.description && (
                            <p className="text-xs text-gray-600 mt-1">{link.description}</p>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          Updated: {link.updated_at ? new Date(link.updated_at).toLocaleDateString() : 'Never'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Expired Users List */}
            <Card className="bg-white border-orange-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2 text-orange-600" />
                  Expired Users Requiring Manual Removal
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Users whose subscriptions have expired but may still be in Telegram groups
                </CardDescription>
              </CardHeader>
              <CardContent>
                {expiredUsers.length === 0 ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-green-800 font-medium">✅ No expired users found</p>
                    <p className="text-sm text-green-600 mt-1">All subscriptions are active or have been removed</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-800 font-medium">
                        ⚠️ {expiredUsers.length} expired user(s) found
                      </p>
                      <p className="text-xs text-orange-600 mt-1">
                        These users need to be manually removed from Telegram groups if they don't have Telegram IDs stored
                      </p>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Name</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Email</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Telegram</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Service</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Expired On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expiredUsers.map((user, index) => (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-2 px-3 font-medium text-gray-900">{user.user_name || 'N/A'}</td>
                              <td className="py-2 px-3 text-gray-600">{user.user_email || 'N/A'}</td>
                              <td className="py-2 px-3">
                                {user.user_telegram_id ? (
                                  <span className="text-blue-600 font-mono text-xs">{user.user_telegram_id}</span>
                                ) : (
                                  <Badge variant="destructive" className="text-xs">No ID</Badge>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <Badge variant="outline" className="text-xs">
                                  {user.subscription_type}
                                </Badge>
                              </td>
                              <td className="py-2 px-3 text-gray-600 text-xs">
                                {user.expired_at ? new Date(user.expired_at).toLocaleString() : 
                                 user.expiry_date ? new Date(user.expiry_date).toLocaleString() : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 font-medium mb-2">📝 Manual Removal Instructions:</p>
                      <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                        <li>Go to your Telegram group/channel admin panel</li>
                        <li>Search for the user by name or Telegram ID</li>
                        <li>Remove or ban the user from the group</li>
                        <li>Users without Telegram ID stored cannot be auto-removed</li>
                      </ol>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Remove Expired Users */}
            <Card className="bg-white border-red-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center">
                  <UserX className="w-4 h-4 mr-2" />
                  Remove Expired Users
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Manually trigger removal of expired users from Telegram groups
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2">⚠️ Important Prerequisites:</h4>
                    <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                      <li>Ensure all Telegram bots are added as <strong>admins</strong> in their respective groups</li>
                      <li>Bots need permissions: Delete messages, Ban users, Restrict members, Invite users via link</li>
                      <li>This action checks all expired subscriptions and removes users automatically</li>
                      <li>Cron job runs daily at midnight to remove expired users automatically</li>
                    </ul>
                  </div>
                  
                  <Button 
                    onClick={handleRemoveExpiredUsers}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Remove Expired Users Now
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Fyers Market Data Token */}
            <FyersTokenCard API_URL={API_URL} />

            {/* Fyers Independent TOTP Auto-Auth (UCS dedicated app) */}
            <FyersIndependentAuthCard API_URL={API_URL} />

            {/* Event Calendar admin */}
            <EventCalendarAdmin API_URL={API_URL} />
          </div>
  );
};

const FyersIndependentAuthCard = ({ API_URL }) => {
  const sessionToken = React.useMemo(() => localStorage.getItem('admin_session_token') || '', []);
  const [status, setStatus] = React.useState(null);
  const [form, setForm] = React.useState({ client_id: '', totp_secret: '', pin: '' });
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const refresh = React.useCallback(async () => {
    if (!sessionToken) return;
    try {
      const r = await fetch(`${API_URL}/api/fyers-auth/status?session_token=${sessionToken}`);
      if (r.ok) setStatus(await r.json());
    } catch (e) { /* ignore */ }
  }, [API_URL, sessionToken]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const saveCreds = async () => {
    setMsg(null);
    if (!form.client_id || !form.totp_secret || !form.pin) { setMsg({ type: 'err', text: 'All 3 fields required' }); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/fyers-auth/credentials`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, session_token: sessionToken }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setMsg({ type: 'ok', text: '✅ Credentials saved & TOTP validated. Click "Run Now" to test the full auth flow.' });
      setForm({ client_id: '', totp_secret: '', pin: '' });
      refresh();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/fyers-auth/run-now?session_token=${sessionToken}`, { method: 'POST' });
      const j = await r.json();
      if (j.status === 'ok') {
        setMsg({ type: 'ok', text: '🎉 TOTP auth ran successfully! Token stored & validated. Refresh /market-intelligence to see live option chain.' });
      } else {
        setMsg({ type: 'err', text: `Failed at step "${j.step || 'unknown'}": ${(j.detail || '').toString().slice(0, 200)}` });
      }
      refresh();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally { setBusy(false); }
  };

  const openManualAuth = async () => {
    const r = await fetch(`${API_URL}/api/fyers-auth/auth-url?session_token=${sessionToken}`);
    const j = await r.json();
    if (j.url) window.open(j.url, '_blank');
  };

  const clearCreds = async () => {
    if (!window.confirm('Clear stored TOTP credentials? Daily auto-auth will stop until reconfigured.')) return;
    await fetch(`${API_URL}/api/fyers-auth/credentials?session_token=${sessionToken}`, { method: 'DELETE' });
    setMsg({ type: 'ok', text: 'Credentials cleared.' });
    refresh();
  };

  return (
    <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-500 shadow-lg" data-testid="fyers-independent-auth-card">
      <CardHeader>
        <CardTitle className="text-gray-900 flex items-center text-xl">
          🔐 Fyers Independent Auto-Auth (UCS App)
        </CardTitle>
        <CardDescription className="text-gray-700">
          Configure once — runs <strong>every weekday at 8:45 AM IST</strong> automatically (staggered after PaperTrade Pro's 8:30 AM auth to avoid Fyers rate-limits). No dependency on PaperTrade Pro.
          <br/>App ID: <code className="bg-white px-1 rounded text-xs">{status?.app_id || 'LD7WS514QL-100'}</code> · Redirect: <code className="bg-white px-1 rounded text-xs break-all">{status?.redirect_uri}</code>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="p-3 bg-white rounded-lg border border-emerald-200 text-sm space-y-1">
          <div>
            <strong>Credentials:</strong>{' '}
            {status?.credentials_set
              ? <span className="text-green-700 font-semibold">● Saved ({status.client_id_preview})</span>
              : <span className="text-red-700 font-semibold">● Not set</span>}
            {status?.credentials_updated_at && <span className="text-xs text-gray-500"> · {new Date(status.credentials_updated_at).toLocaleString()}</span>}
          </div>
          <div>
            <strong>Token:</strong>{' '}
            {status?.token_set
              ? <span className="text-green-700 font-semibold">● Active ({status.token_source || 'unknown source'})</span>
              : <span className="text-red-700 font-semibold">● Not yet generated</span>}
            {status?.token_updated_at && <span className="text-xs text-gray-500"> · {new Date(status.token_updated_at).toLocaleString()}</span>}
          </div>
          {status?.last_run && (
            <div>
              <strong>Last auto-run:</strong>{' '}
              <span className={status.last_run.status === 'ok' ? 'text-green-700' : 'text-red-700'}>
                {status.last_run.status} {status.last_run.step ? `(${status.last_run.step})` : ''}
              </span>
              {status.last_run.ran_at && <span className="text-xs text-gray-500"> · {new Date(status.last_run.ran_at).toLocaleString()}</span>}
              {status.last_run.detail && <div className="text-xs text-gray-500 truncate">Detail: {status.last_run.detail}</div>}
            </div>
          )}
        </div>

        {!status?.credentials_set && (
          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Input placeholder="Fyers Client ID (e.g. XP33698)" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="fyers-totp-client-id" />
              <Input type="password" placeholder="TOTP Secret (Base32)" value={form.totp_secret} onChange={(e) => setForm({ ...form, totp_secret: e.target.value })} data-testid="fyers-totp-secret" />
              <Input type="password" placeholder="4-digit PIN" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} data-testid="fyers-totp-pin" />
            </div>
            <div className="text-xs text-gray-600">
              💡 TOTP secret is the Base32 seed you saved when you set up Fyers 2FA (looks like <code>E3UICUBD...</code>). 
              All 3 values are stored securely in MongoDB; admin-only access.
            </div>
            <Button onClick={saveCreds} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fyers-totp-save-btn">
              {busy ? 'Saving…' : 'Save & Validate Credentials'}
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-emerald-200">
          {status?.credentials_set && (
            <>
              <Button onClick={runNow} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fyers-totp-run-now">
                {busy ? 'Running…' : '⚡ Run TOTP Auth Now'}
              </Button>
              <Button onClick={clearCreds} variant="outline" className="border-red-400 text-red-700">Clear Credentials</Button>
            </>
          )}
          <Button onClick={openManualAuth} variant="outline" className="border-emerald-400 text-emerald-700">
            Manual OAuth (fallback)
          </Button>
        </div>

        {msg && (
          <div className={`p-3 rounded-lg text-sm ${msg.type === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {msg.text}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EventCalendarAdmin = ({ API_URL }) => {
  const sessionToken = React.useMemo(() => localStorage.getItem('admin_session_token') || '', []);
  const [events, setEvents] = React.useState([]);
  const [form, setForm] = React.useState({
    title: '', event_date: '', event_time: '', category: 'rbi', importance: 'medium', description: '', link: ''
  });
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/market-intelligence/events?preview=true&days=120`);
      const j = await r.json();
      setEvents(j.events || []);
    } catch (e) { /* ignore */ }
  }, [API_URL]);

  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    setMsg(null);
    if (!form.title || !form.event_date) { setMsg({ type: 'err', text: 'Title and date are required' }); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/market-intelligence/events?session_token=${sessionToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed');
      }
      setMsg({ type: 'ok', text: 'Event added!' });
      setForm({ title: '', event_date: '', event_time: '', category: 'rbi', importance: 'medium', description: '', link: '' });
      load();
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await fetch(`${API_URL}/api/market-intelligence/events/${id}?session_token=${sessionToken}`, { method: 'DELETE' });
    load();
  };

  return (
    <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-indigo-400 shadow-lg" data-testid="admin-event-calendar-card">
      <CardHeader>
        <CardTitle className="text-gray-900 flex items-center text-xl">
          📅 Event Calendar — Admin
        </CardTitle>
        <CardDescription className="text-gray-700">
          Add upcoming market events (RBI policy, FOMC, results, IPOs) visible to Premium subscribers on the <strong>/market-intelligence</strong> page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input placeholder="Event title (e.g., RBI MPC Meeting)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-event-title" />
          <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="admin-event-date" />
          <Input placeholder="Time (HH:MM IST, optional)" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })} />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white border border-indigo-300 rounded-lg px-3 py-2 text-sm" data-testid="admin-event-category">
            <option value="rbi">RBI Policy</option>
            <option value="fomc">FOMC</option>
            <option value="earnings">Earnings</option>
            <option value="budget">Budget</option>
            <option value="macro">Macro Data</option>
            <option value="ipo">IPO</option>
          </select>
          <select value={form.importance} onChange={(e) => setForm({ ...form, importance: e.target.value })} className="bg-white border border-indigo-300 rounded-lg px-3 py-2 text-sm" data-testid="admin-event-importance">
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <Input placeholder="Link (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
        </div>
        <Input placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Button onClick={create} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="admin-event-create-btn">
          {busy ? 'Adding…' : 'Add Event'}
        </Button>
        {msg && (
          <div className={`p-2 rounded text-xs ${msg.type === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {msg.text}
          </div>
        )}
        <div className="border-t border-indigo-200 pt-3">
          <div className="text-sm font-semibold text-gray-800 mb-2">Upcoming events ({events.length})</div>
          {events.length === 0 ? (
            <div className="text-xs text-gray-500">No events yet.</div>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {events.map((e, i) => (
                <div key={e.id || i} className="flex items-center justify-between bg-white rounded border border-indigo-200 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{e.title}</div>
                    <div className="text-xs text-gray-500">{e.event_date} · {e.category} · {e.importance}</div>
                  </div>
                  {e.id && (
                    <button onClick={() => remove(e.id)} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`admin-event-delete-${e.id}`}>
                      🗑
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const FyersTokenCard = ({ API_URL }) => {
  const [status, setStatus] = React.useState(null);
  const [token, setToken] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [authUrl, setAuthUrl] = React.useState(null);

  const sessionToken = React.useMemo(() => localStorage.getItem('admin_session_token') || '', []);

  const refresh = React.useCallback(async () => {
    if (!sessionToken) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/market-data/fyers-status?session_token=${sessionToken}`);
      if (r.ok) setStatus(await r.json());
    } catch (e) { /* ignore */ }
  }, [API_URL, sessionToken]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const handleSave = async () => {
    if (!token.trim()) { setMsg({ type: 'err', text: 'Please paste an access token' }); return; }
    setLoading(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/market-data/fyers-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token.trim(), session_token: sessionToken })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setMsg({ type: 'ok', text: 'Token saved & validated successfully!' });
      setToken('');
      refresh();
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally { setLoading(false); }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear stored Fyers token?')) return;
    try {
      await fetch(`${API_URL}/api/admin/market-data/fyers-token?session_token=${sessionToken}`, { method: 'DELETE' });
      setMsg({ type: 'ok', text: 'Token cleared.' });
      refresh();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
  };

  const handleGetAuthUrl = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/market-data/fyers-authcode-url?session_token=${sessionToken}`);
      const j = await r.json();
      setAuthUrl(j);
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
  };

  return (
    <Card className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-amber-400 shadow-lg">
      <CardHeader>
        <CardTitle className="text-gray-900 flex items-center text-xl">
          <Activity className="w-5 h-5 mr-2 text-amber-600" />
          Fyers Market Data — Daily Access Token
        </CardTitle>
        <CardDescription className="text-gray-700">
          Powers live indices & option-chain on the public <strong>/market-intelligence</strong> page. Fyers tokens expire daily — paste a fresh one here every trading day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="p-3 bg-white rounded-lg border border-amber-200">
          <div className="text-sm text-gray-700">
            Current status:{' '}
            {status?.token_set ? (
              <span className="text-green-700 font-semibold" data-testid="fyers-token-status-active">● Token Active ({status.token_preview})</span>
            ) : (
              <span className="text-red-700 font-semibold" data-testid="fyers-token-status-missing">● No token set (Yahoo Finance fallback only)</span>
            )}
          </div>
          {status?.updated_at && <div className="text-xs text-gray-500 mt-1">Last updated: {new Date(status.updated_at).toLocaleString()}</div>}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="fyers-token">Paste daily Access Token</Label>
          <Input
            id="fyers-token"
            type="password"
            placeholder="eyJ0eXAiOiJKV1QiLCJhbGc..."
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="fyers-token-input"
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSave} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="fyers-token-save-btn">
              {loading ? 'Validating...' : 'Save & Validate'}
            </Button>
            <Button onClick={handleGetAuthUrl} variant="outline" className="border-amber-400 text-amber-700" data-testid="fyers-auth-url-btn">
              <ExternalLink className="w-4 h-4 mr-1" /> Get Auth URL
            </Button>
            {status?.token_set && (
              <Button onClick={handleClear} variant="outline" className="border-red-400 text-red-700">
                Clear Token
              </Button>
            )}
          </div>
        </div>

        {authUrl && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-700 space-y-2">
            <div className="font-semibold text-blue-900">Steps to get today's token:</div>
            <div className="text-xs whitespace-pre-line">{authUrl.instructions}</div>
            <a href={authUrl.url} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all text-xs">{authUrl.url}</a>
          </div>
        )}

        {msg && (
          <div className={`p-3 rounded-lg text-sm ${msg.type === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {msg.text}
          </div>
        )}

        <FyersAutoSyncSection API_URL={API_URL} sessionToken={sessionToken} />
      </CardContent>
    </Card>
  );
};

const FyersAutoSyncSection = ({ API_URL, sessionToken }) => {
  const [src, setSrc] = React.useState(null);
  const [form, setForm] = React.useState({ source_url: '', auth_header: '', json_path: 'access_token' });
  const [msg, setMsg] = React.useState(null);
  const [syncing, setSyncing] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!sessionToken) return;
    try {
      const r = await fetch(`${API_URL}/api/market-intelligence/fyers-token-source?session_token=${sessionToken}`);
      if (r.ok) setSrc(await r.json());
    } catch (e) { /* ignore */ }
  }, [API_URL, sessionToken]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    setMsg(null);
    if (!form.source_url.trim()) { setMsg({ type: 'err', text: 'Source URL required' }); return; }
    setSyncing(true);
    try {
      const r = await fetch(`${API_URL}/api/market-intelligence/fyers-token-source`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, session_token: sessionToken }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      const status = j?.immediate_sync?.status;
      if (status === 'ok') setMsg({ type: 'ok', text: 'Source saved & token synced!' });
      else setMsg({ type: 'err', text: `Source saved but sync failed: ${JSON.stringify(j.immediate_sync)}` });
      refresh();
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally { setSyncing(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API_URL}/api/market-intelligence/fyers-token-source/sync?session_token=${sessionToken}`, { method: 'POST' });
      const j = await r.json();
      setMsg({ type: j.status === 'ok' ? 'ok' : 'err', text: `Sync result: ${JSON.stringify(j)}` });
    } finally { setSyncing(false); }
  };

  const clear = async () => {
    if (!window.confirm('Clear auto-sync configuration?')) return;
    await fetch(`${API_URL}/api/market-intelligence/fyers-token-source?session_token=${sessionToken}`, { method: 'DELETE' });
    setMsg({ type: 'ok', text: 'Cleared.' });
    refresh();
  };

  return (
    <div className="border-t border-amber-200 pt-3 mt-3">
      <div className="font-semibold text-gray-900 mb-2">⚡ Auto-Sync Token (optional)</div>
      <div className="text-xs text-gray-600 mb-2">
        Reuse your existing daily Fyers auth (e.g., from <code className="bg-gray-100 px-1 rounded">api.uppercircuitstocks.com</code>).
        Server will poll this URL every 30 min and fetch the current token automatically — zero daily manual work.
      </div>
      <div className="p-2 bg-white rounded border border-amber-200 mb-2 text-xs">
        {src?.configured ? (
          <span className="text-green-700">● Configured: <code>{src.source_url}</code> (path: {src.json_path})</span>
        ) : (
          <span className="text-gray-600">No auto-sync source configured.</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input placeholder="Source URL (e.g., https://api.uppercircuitstocks.com/api/fyers/token)" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="fyers-source-url" />
        <Input placeholder="Auth header (optional, e.g., Bearer xxx)" value={form.auth_header} onChange={(e) => setForm({ ...form, auth_header: e.target.value })} data-testid="fyers-source-auth" />
        <Input placeholder="JSON path (default: access_token)" value={form.json_path} onChange={(e) => setForm({ ...form, json_path: e.target.value })} data-testid="fyers-source-jsonpath" />
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <Button onClick={save} disabled={syncing} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="fyers-source-save-btn">
          {syncing ? 'Saving…' : 'Save & Sync Now'}
        </Button>
        {src?.configured && (
          <>
            <Button onClick={syncNow} variant="outline" className="border-amber-400 text-amber-700">Sync Now</Button>
            <Button onClick={clear} variant="outline" className="border-red-400 text-red-700">Clear</Button>
          </>
        )}
      </div>
      {msg && (
        <div className={`mt-2 p-2 rounded text-xs ${msg.type === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
